import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { TextCell } from '../../../../components/table/TextCell';
import { StatusText } from '../../../../components/table/StatusText';
import { formatDateOnly } from '../../../../lib/formatDate';
import { leaveCreditApi } from '../../leave/leaveApi';
import {
  LEAVE_BALANCE_DISCREPANCY_TEXT,
  LEAVE_ENTRY_STATUS_LABELS,
  LEAVE_ENTRY_TYPE_LABELS,
  LeaveEntryStatus,
  type LeaveCreditBalance,
  type LeaveLedgerEntry,
} from '../../leave/leaveTypes';
import { LeaveFigure } from '../../leave/components/LeaveFigure';

interface Line {
  entry: LeaveLedgerEntry;
  /** After this row, approved rows only — null on a row that does not count. */
  running: number | null;
}

/**
 * The ledger behind ONE balance — opened from the Credit Balance table's Balance figure.
 *
 * **Every live row the figure is made of**: the type's own, plus the Used rows of children
 * that SHARE its credit, because the parent's balance counts them. So the last running figure
 * equals the balance clicked.
 *
 * **Oldest first, with a running balance** — the order the server's "went below zero" check
 * reads in (effective date, credits before debits on one day, then when recorded), so the row
 * where a flagged balance dipped is the one showing a negative figure.
 *
 * Writes nothing: no confirm tick, no primary action, Close only.
 */
export function LeaveTypeLedgerModal({
  personnelId,
  row,
  onClose,
}: {
  personnelId: string;
  row: LeaveCreditBalance;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<LeaveLedgerEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  /*
    The WHOLE balance list, fetched here rather than handed down: the table's rows are the
    searched page, and a parent can be on screen while the children sharing its credit are not —
    their Used rows would then be missing and the last running figure would not be the balance.
  */
  const [balances, setBalances] = useState<LeaveCreditBalance[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      leaveCreditApi.ledger(personnelId, false, controller.signal),
      leaveCreditApi.balances(personnelId, controller.signal),
    ])
      .then(([ledger, all]) => {
        setBalances(all);
        setEntries(ledger);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [personnelId]);

  // The type itself and every child drawing on its credit.
  const typeIds = useMemo(
    () => new Set([
      row.leaveTypeId,
      ...balances
        .filter((b) => b.sharesParentCredit && b.parentLeaveTypeId === row.leaveTypeId)
        .map((b) => b.leaveTypeId),
    ]),
    [balances, row.leaveTypeId],
  );

  const sharing = typeIds.size > 1;

  const lines = useMemo<Line[]>(() => {
    if (!entries) return [];

    const mine = entries
      .filter((e) => e.isActive && typeIds.has(e.leaveTypeId))
      .sort((a, b) =>
        a.effectiveDate.localeCompare(b.effectiveDate)
        || b.days - a.days
        || a.createdAt.localeCompare(b.createdAt));

    let running = 0;

    return mine.map((entry) => {
      if (entry.status !== LeaveEntryStatus.Approved) return { entry, running: null };
      running += entry.days;
      return { entry, running };
    });
  }, [entries, typeIds]);

  const flags = row.discrepancies ?? [];

  // The row as just fetched, so the summary agrees with the lines under it; the clicked one until then.
  const summary = balances.find((b) => b.leaveTypeId === row.leaveTypeId) ?? row;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${row.leaveTypeName} — ledger`}
      // 3xl: seven columns, ~970px, and a date that must stay on one line.
      size="3xl"
      id="leave-type-ledger-modal"
      footer={
        <Button id="leave-type-ledger-close" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {flags.length > 0 ? (
          <div role="note" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs font-medium text-red-700">Possible discrepancy</p>
            <ul className="mt-1 list-disc pl-4 text-xs text-red-700">
              {flags.map((flag) => (
                <li key={flag}>{LEAVE_BALANCE_DISCREPANCY_TEXT[flag]}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {/*
          The Credit Balance row this ledger adds up to (product call, 27 Oct 2026) — the table's
          own five headings and figures, so the reader checks the lines against it in place.
        */}
        <PreviewGrid<LeaveCreditBalance>
          rows={[summary]}
          rowKey={(b) => b.leaveTypeId}
          empty="No balance for this leave type."
          columns={[
            { header: 'Beginning', width: 100, align: 'right', render: (b) => <LeaveFigure value={b.beginning} /> },
            { header: 'Earned', width: 100, align: 'right', render: (b) => <LeaveFigure value={b.earned} signed /> },
            { header: 'Used', width: 100, align: 'right', render: (b) => <LeaveFigure value={b.used} debit /> },
            { header: 'Adjusted', width: 100, align: 'right', render: (b) => <LeaveFigure value={b.adjusted} /> },
            {
              header: 'Balance',
              width: 100,
              align: 'right',
              render: (b) =>
                b.balance == null ? (
                  <LeaveFigure value={null} />
                ) : (
                  <span className={`font-mono tabular-nums text-xs ${b.balance < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                    {b.balance.toFixed(2)}
                  </span>
                ),
            },
          ]}
        />

        {failed ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">The ledger could not be loaded.</p>
          </div>
        ) : (
          <PreviewGrid<Line>
            rows={entries ? lines : []}
            rowKey={(line) => line.entry.id}
            empty={entries ? 'Nothing has been recorded against this leave type yet.' : 'Loading…'}
            columns={[
              {
                header: 'Date',
                width: 150,
                render: (line) => <TextCell value={formatDateOnly(line.entry.effectiveDate)} mono />,
              },
              // Only where children share the credit: otherwise every row names the same type.
              ...(sharing
                ? [{
                    header: 'Type',
                    width: 180,
                    render: (line: Line) => <TextCell value={line.entry.leaveTypeName} />,
                  }]
                : []),
              {
                header: 'Entry',
                width: 140,
                render: (line) => <TextCell value={LEAVE_ENTRY_TYPE_LABELS[line.entry.entryType]} />,
              },
              {
                header: 'Days',
                width: 90,
                align: 'right',
                render: (line) => (
                  <span className={`font-mono tabular-nums text-xs ${line.entry.days < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {line.entry.days > 0 ? '+' : ''}
                    {line.entry.days.toFixed(2)}
                  </span>
                ),
              },
              {
                header: 'Balance',
                width: 100,
                align: 'right',
                render: (line) =>
                  line.running == null ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <span className={`font-mono tabular-nums text-xs ${line.running < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                      {line.running.toFixed(2)}
                    </span>
                  ),
              },
              {
                header: 'Reason',
                width: 200,
                render: (line) => <TextCell value={line.entry.reason} />,
              },
              {
                header: 'Status',
                width: 110,
                render: (line) => (
                  <StatusText tone={line.entry.status === LeaveEntryStatus.Approved ? 'active' : 'inactive'}>
                    {LEAVE_ENTRY_STATUS_LABELS[line.entry.status]}
                  </StatusText>
                ),
              },
            ]}
          />
        )}
      </div>
    </Modal>
  );
}
